import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiErrorMessage } from '@milliswift/auth'
import {
  authApi,
  tenantQueries,
  notificationQueries,
  deviceQueries,
  queryKeys,
} from '@milliswift/api-client'
import {
  ActivityFeed,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  DescriptionList,
  EmptyState,
  Meter,
  PageSkeleton,
  SectionHeader,
  StatCard,
  useToast,
} from '@milliswift/ui'
import { AlertTriangle, Database, KeyRound, Monitor, Zap } from 'lucide-react'

const formatBytes = (gb: number): string =>
  gb >= 1024 ? `${(gb / 1024).toFixed(1)} TB` : `${gb.toFixed(1)} GB`

/**
 * Account summary: plan, quota meters, and a recent-activity feed.
 *
 * Quota numbers come from GET /account/quota — one call with limits, usage and
 * the exceeded flag together, which is exactly what the meters need.
 */

/** Cancels a scheduled deletion from the banner. */
function CancelDeletionButton() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const cancel = useMutation({
    mutationFn: () => authApi.cancelDeletion(),
    onSuccess: () => {
      toast.success('Deletion cancelled')
      void queryClient.invalidateQueries({ queryKey: queryKeys.account })
    },
    onError: (error) => toast.error('Could not cancel', getApiErrorMessage(error)),
  })

  return (
    <Button size="sm" variant="outline" loading={cancel.isPending} onClick={() => cancel.mutate()}>
      Cancel deletion
    </Button>
  )
}

export default function Overview() {
  const account = useQuery(tenantQueries.account())
  const quota = useQuery(tenantQueries.quota())
  const devices = useQuery(deviceQueries.list())
  const notifications = useQuery(notificationQueries.list({ limit: 8 }))

  if (account.isPending || quota.isPending) return <PageSkeleton />

  if (account.isError) {
    return (
      <Callout tone="danger" title="Could not load your account">
        {getApiErrorMessage(account.error)}
      </Callout>
    )
  }

  const acct = account.data
  const q = quota.data
  const activeDevices = devices.data?.active.length ?? 0
  const pendingDevices = devices.data?.pending.length ?? 0

  return (
    <div className="space-y-8">
      <SectionHeader
        title="Overview"
        description={`Account ${acct.account_id}`}
        action={
          <Button variant="outline" size="sm" onClick={() => void account.refetch()}>
            Refresh
          </Button>
        }
      />

      {acct.deletion_scheduled_at ? (
        <Callout
          tone="danger"
          title="This account is scheduled for deletion"
          action={
            <CancelDeletionButton />
          }
        >
          Scheduled for {new Date(acct.deletion_scheduled_at).toLocaleDateString()}. Data will be
          permanently destroyed after the grace period. You can cancel from the Security page until
          it ends.
        </Callout>
      ) : null}

      {q?.quota_exceeded_kind ? (
        <Callout tone="warning" title="Plan limit reached" icon={<AlertTriangle className="h-4 w-4" />}>
          {q.quota_exceeded_kind === 'rows'
            ? 'Queries are paused until the next period or an upgrade.'
            : 'Writes are paused until the next period or an upgrade.'}
        </Callout>
      ) : null}

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-card border border-line bg-surface p-5 shadow-card">
          <p className="text-caption font-medium uppercase tracking-widest text-subtle">Plan</p>
          <p className="mt-3 font-mono text-2xl font-semibold capitalize text-foreground">
            {acct.plan}
          </p>
          <Badge className="mt-2" tone={acct.status === 'active' ? 'success' : 'warning'} dot>
            {acct.status}
          </Badge>
        </div>

        <StatCard
          label="Members"
          value={acct.member_count}
          hint="on this account"
          icon={<KeyRound />}
        />
        <StatCard
          label="Devices"
          value={activeDevices}
          hint={pendingDevices > 0 ? `${pendingDevices} pending approval` : 'all approved'}
          icon={<Monitor />}
        />
        <StatCard label="Databases" value={acct.device_count} hint="across all regions" icon={<Database />} />
      </div>

      {/* Quota meters */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Usage this period</CardTitle>
            <p className="mt-0.5 text-caption text-muted">
              Since {q ? new Date(q.period_start).toLocaleDateString() : '—'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => (window.location.href = '/databases')}>
            Manage
          </Button>
        </CardHeader>
        <CardBody className="space-y-5">
          {q ? (
            <>
              <Meter
                value={q.rows_percent_used}
                max={100}
                label="Rows scanned"
                format={() => `${q.rows_scanned.toLocaleString()} rows · ${q.rows_percent_used.toFixed(0)}%`}
              />
              <Meter
                value={q.gb_stored}
                max={Math.max(q.gb_stored * 1.5, 10)}
                label="Storage"
                format={(v, m) => `${formatBytes(v)} of ${formatBytes(m)}`}
              />
              <div className="grid gap-4 sm:grid-cols-3">
                <DescriptionList
                  items={[
                    { term: 'Queries', detail: q.query_count.toLocaleString() },
                    { term: 'Written', detail: formatBytes(q.gb_written) },
                  ]}
                />
              </div>
            </>
          ) : (
            <p className="text-sm text-muted">No usage recorded yet this period.</p>
          )}
        </CardBody>
      </Card>

      {/* Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent activity</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => (window.location.href = '/notifications')}>
            View all
          </Button>
        </CardHeader>
        <CardBody>
          {notifications.data && notifications.data.length > 0 ? (
            <ActivityFeed
              items={notifications.data.slice(0, 8).map((item) => ({
                id: item.id,
                icon: item.category === 'devices' ? <Monitor /> : item.category === 'billing' ? <Zap /> : <KeyRound />,
                title: item.title,
                meta: item.body,
                timestamp: new Date(item.created_at).toLocaleString(),
              }))}
            />
          ) : (
            <EmptyState
              icon={<Zap />}
              title="Nothing yet"
              description="Account activity — sign-ins, device approvals, quota events — will appear here."
            />
          )}
        </CardBody>
      </Card>
    </div>
  )
}
